import { For, Show, createMemo } from "solid-js";
import { Portal } from "solid-js/web";
import { listShortcuts, useShortcut } from "../hooks/useShortcut";
import { useTranslation } from "../hooks/useTranslation";
import Text from "./Text";
import styles from "./ShortcutCheatsheet.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
}

function renderKey(part: string) {
  if (part === "mod") return navigator.platform.includes("Mac") ? "⌘" : "Ctrl";
  if (part === "shift") return "Shift";
  if (part === "alt") return "Alt";
  if (part === "space") return "Space";
  if (part === "escape") return "Esc";
  if (part === "enter") return "Enter";
  if (part === "arrowup") return "↑";
  if (part === "arrowdown") return "↓";
  if (part === "arrowleft") return "←";
  if (part === "arrowright") return "→";
  return part.toUpperCase();
}

function renderCombo(combo: string) {
  return combo.split(/\s+/).map((seg) => seg.split("+").map(renderKey).join(" + ")).join("  then  ");
}

export function ShortcutCheatsheet(props: Props) {
  const { t } = useTranslation();
  useShortcut("escape", () => { if (props.open) props.onClose(); }, { description: "Close cheatsheet", scope: "cheatsheet" });

  const grouped = createMemo(() => {
    const all = listShortcuts().filter((s) => s.description);
    const out = new Map<string, typeof all>();
    for (const s of all) {
      if (!out.has(s.scope)) out.set(s.scope, []);
      out.get(s.scope)!.push(s);
    }
    return [...out.entries()];
  });

  return (
    <Show when={props.open}>
      <Portal mount={document.body}>
        <div class={styles.backdrop} onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }}>
          <div class={styles.panel} role="dialog" aria-label={t("KEYBOARD_SHORTCUTS")}>
            <div class={styles.header}>
              <h2><Text val="KEYBOARD_SHORTCUTS" /></h2>
              <button class={styles.close} onClick={props.onClose} aria-label={t("CLOSE")}>
                <i class="fa-solid fa-xmark" />
              </button>
            </div>
            <div class={styles.list}>
              <For each={grouped()}>
                {([scope, items]) => (
                  <div class={styles.section}>
                    <div class={styles.sectionTitle}>{scope}</div>
                    <For each={items}>
                      {(s) => (
                        <div class={styles.row}>
                          <div class={styles.desc}>{s.description}</div>
                          <div class={styles.combo}>
                            <For each={renderCombo(s.combo).split(/\s+/)}>
                              {(k) => k === "+" || k === "then" ? <span style={{ color: "var(--dark-text-muted)" }}>{k}</span> : <kbd class={styles.kbd}>{k}</kbd>}
                            </For>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                )}
              </For>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
